import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  Checkbox,
  Dialog,
  CircularProgress,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import type { Insight } from '../InsightsTable/InsightsTable'
import { ShareCard, useShareCardInk } from '../ShareCard/ShareCard'
import { ShareCardFormat, ShareCardLayout, ShareCardSize } from '../ShareCard/ShareCard.types'
import { buildFilename } from './fileName'
import { rasterizeShareCard } from './rasterizeShareCard'
import { trackCopyImage, trackDownloadPng, useTrackingContext } from '../../../../analytics'
import type { LabelType, ShareCardSettingsProps } from '../../../../analytics'
import {
  FORMAT_DIMENSIONS,
  FORMAT_DIMENSION_LABEL,
  FORMAT_LABEL,
  IMAGE_FORMATS,
  INK_SWATCHES,
  DEFAULT_INK,
  LAYOUT_GRADIENT,
} from '../ShareCard/shareCardTokens'
import { CONTRAST_WARNING } from '../ShareCard/shareCardContrast'

const LAYOUTS: { value: ShareCardLayout; label: string }[] = [
  { value: 'stat', label: 'Stat' },
  { value: 'split', label: 'Split' },
  { value: 'plaque', label: 'Plaque' },
]

const SIZES: { value: ShareCardSize; label: string }[] = [
  { value: 'S', label: 'Small' },
  { value: 'M', label: 'Medium' },
  { value: 'L', label: 'Large' },
]

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

const isClipboardSupported = () =>
  typeof navigator !== 'undefined' && !!navigator.clipboard?.write && typeof (window as any).ClipboardItem !== 'undefined'

interface DownloadInsightModalProps {
  open: boolean
  onClose: () => void
  insight: Insight
  brandId: string
  kpi: string
  workspaceName: string
  kpiDisplay: string
  performanceDateGte: string | null | undefined
  performanceDateLte: string | null | undefined
}

/** Toast: fixed, bottom-center, auto-dismisses after 2600ms. See design handoff "Toast". */
const Toast = ({ message }: { message: string }) => (
  <div
    style={{
      position: 'fixed',
      left: '50%',
      bottom: 28,
      transform: 'translateX(-50%)',
      zIndex: 1700,
      background: '#051E33',
      color: '#fff',
      borderRadius: 4,
      padding: '12px 20px',
      fontSize: '0.875rem',
      boxShadow: '0 6px 16px rgba(0,0,0,0.3)',
    }}
  >
    {message}
  </div>
)

export const DownloadInsightModal = ({
  open,
  onClose,
  insight,
  brandId,
  kpi,
  workspaceName,
  kpiDisplay,
  performanceDateGte,
  performanceDateLte,
}: DownloadInsightModalProps) => {
  const ctx = useTrackingContext()
  const clipboardSupported = isClipboardSupported()
  const renderTargets = useRef<Partial<Record<ShareCardFormat, HTMLDivElement | null>>>({})

  const [masked, setMasked] = useState(true)
  const [brandText, setBrandText] = useState('This Brand')
  const [layout, setLayout] = useState<ShareCardLayout>('stat')
  const [size, setSize] = useState<ShareCardSize>('M')
  const [color, setColor] = useState<string>(DEFAULT_INK)
  const [formats, setFormats] = useState<ShareCardFormat[]>(['wide'])
  const [previewFormat, setPreviewFormat] = useState<ShareCardFormat>('wide')
  const [done, setDone] = useState(false)
  const [downloaded, setDownloaded] = useState<{ name: string }[]>([])
  const [copied, setCopied] = useState(false)
  const [toast, setToast] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  // Reset to the editor state whenever a new row is opened.
  useEffect(() => {
    if (open) {
      setDone(false)
      setCopied(false)
      setError('')
    }
  }, [open, insight.id])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 2600)
    return () => clearTimeout(t)
  }, [toast])

  const brandLine = masked ? brandText || 'This Brand' : workspaceName
  const better = !insight.labelLift || parseFloat(insight.labelLift) > 0 ? 'better' : 'worse'
  const sentence = `Ads with this label performed ${insight.labelLift ?? '-'} ${better} than ads without this label in terms of ${kpiDisplay}.`

  const cardProps = {
    layout,
    format: previewFormat,
    size,
    color,
    headline: insight.labelName ?? '',
    labelType: insight.labelType ?? '',
    stat: insight.labelLift ?? '-',
    sentence,
    brandLine,
  }

  const { fellBack } = useShareCardInk(layout, color)

  const filenameParts = {
    insightLabel: insight.labelName ?? insight.id,
    targetKpiLabel: kpiDisplay,
    performanceDateGte: performanceDateGte ?? new Date().toISOString().slice(0, 10),
    performanceDateLte: performanceDateLte ?? new Date().toISOString().slice(0, 10),
  }

  const renderPng = async (format: ShareCardFormat): Promise<Blob> => {
    const node = renderTargets.current[format]
    if (!node) throw new Error(`No render target for ${format}`)
    return rasterizeShareCard(node, format)
  }

  const handleToggleFormat = (format: ShareCardFormat) => {
    if (formats.includes(format)) {
      // Never allow an empty selection.
      if (formats.length === 1) return
      const next = formats.filter((f) => f !== format)
      setFormats(next)
      if (previewFormat === format) setPreviewFormat(next[0])
      return
    }
    setFormats([...formats, format])
    setPreviewFormat(format)
  }

  const settingsEventProps = (): ShareCardSettingsProps => ({
    brand_id: brandId,
    account_id: ctx.accountId,
    kpi,
    label_name: insight.labelName ?? '',
    label_type: (insight.rawLabelType ?? '') as LabelType,
    label_lift: insight.labelLift ?? '',
    layout,
    size,
    color,
    brand_name_hidden: masked,
  })

  const handleDownload = async () => {
    setBusy(true)
    setError('')
    trackDownloadPng({ ...settingsEventProps(), formats, format_count: formats.length }, ctx)
    try {
      const files: { name: string }[] = []
      for (const format of formats) {
        const blob = await renderPng(format)
        const filename = buildFilename(filenameParts, format)
        triggerDownload(blob, filename)
        files.push({ name: filename })
        // Stagger multi-file downloads so browsers don't block the 2nd/3rd trigger.
        if (formats.length > 1) await new Promise((r) => setTimeout(r, 300))
      }
      setDownloaded(files)
      setDone(true)
    } catch (e) {
      setError('Something went wrong rendering the image. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const handleCopyImage = () => {
    if (!clipboardSupported) return
    setError('')
    trackCopyImage({ ...settingsEventProps(), format: previewFormat }, ctx)
    // Safari requires the ClipboardItem's promise to be handed over synchronously,
    // inside the user-gesture handler — not after an intervening await.
    const item = new (window as any).ClipboardItem({ 'image/png': renderPng(previewFormat) })
    navigator.clipboard
      .write([item])
      .then(() => {
        setCopied(true)
        setToast(`${FORMAT_LABEL[previewFormat]} image copied to clipboard`)
      })
      .catch(() => setError('Could not copy the image. Please try downloading instead.'))
  }

  const handleClose = () => {
    setCopied(false)
    onClose()
  }

  const countLabel = formats.length === 1 ? 'Download PNG' : `Download ${formats.length} PNGs`

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        PaperProps={{ sx: { width: 1040, maxWidth: '100%', maxHeight: '100%', borderRadius: '4px' } }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          <div style={{ width: 400, minWidth: 320, flex: 1, borderRight: '1px solid #e0e0e0', padding: 32, display: 'flex', flexDirection: 'column', gap: 28 }}>
            {done ? (
              <ConfirmationPane
                downloaded={downloaded}
                onDone={handleClose}
                onEditSettings={() => setDone(false)}
                onPickAnotherSize={(format) => {
                  setFormats([format])
                  setPreviewFormat(format)
                  setDone(false)
                }}
              />
            ) : (
              <>
                <div>
                  <h2 style={{ fontFamily: 'Roobert', fontSize: '1.375rem', fontWeight: 500, margin: 0 }}>Download insight</h2>
                  <p style={{ fontSize: '0.875rem', color: '#4e4e4e', lineHeight: 1.5, margin: '4px 0 0' }}>
                    Saves image files to your computer.
                  </p>
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.9375rem', fontWeight: 600 }}>Hide brand name</span>
                    <Toggle checked={masked} onChange={setMasked} />
                  </div>
                  <input
                    type="text"
                    value={masked ? brandText : workspaceName}
                    disabled={!masked}
                    onChange={(e) => setBrandText(e.target.value)}
                    style={{
                      width: '100%',
                      marginTop: 8,
                      padding: '9px 12px',
                      borderRadius: 6,
                      outline: '1px solid #d1d5db',
                      outlineOffset: -1,
                      border: 'none',
                      fontSize: '0.875rem',
                      color: masked ? '#051E33' : '#888888',
                      background: masked ? '#fff' : '#f0f0f0',
                      boxSizing: 'border-box',
                    }}
                  />
                  <p style={{ fontSize: '0.78125rem', color: '#888888', margin: '6px 0 0' }}>
                    {masked ? `Shown instead of "${workspaceName}". Baked into the image file.` : 'Brand name will appear in the downloaded image.'}
                  </p>
                </div>

                <div>
                  <span style={{ fontSize: '0.9375rem', fontWeight: 600 }}>Image layout</span>
                  <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                    {LAYOUTS.map(({ value, label }) => (
                      <button
                        key={value}
                        onClick={() => setLayout(value)}
                        style={{
                          flex: 1,
                          padding: 8,
                          borderRadius: 6,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 6,
                          background: '#fff',
                          cursor: 'pointer',
                          border: layout === value ? '2px solid #0454AE' : '1px solid #d1d5db',
                        }}
                      >
                        <span style={{ width: '100%', height: 30, borderRadius: 3, border: '1px solid #e0e0e0', background: LAYOUT_GRADIENT[value] }} />
                        <span style={{ fontSize: '0.75rem', color: layout === value ? '#0454AE' : '#4e4e4e' }}>{label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <span style={{ fontSize: '0.9375rem', fontWeight: 600 }}>Headline & stat size</span>
                  <div style={{ display: 'flex', border: '1px solid #d1d5db', borderRadius: 6, overflow: 'hidden', marginTop: 8 }}>
                    {SIZES.map(({ value, label }, i) => (
                      <button
                        key={value}
                        onClick={() => setSize(value)}
                        style={{
                          flex: 1,
                          padding: '8px 0',
                          fontSize: '0.84375rem',
                          cursor: 'pointer',
                          border: 'none',
                          borderLeft: i === 1 ? '1px solid #d1d5db' : undefined,
                          borderRight: i === 1 ? '1px solid #d1d5db' : undefined,
                          background: size === value ? 'rgba(4,84,174,0.08)' : '#fff',
                          color: size === value ? '#0454AE' : '#4e4e4e',
                          fontWeight: size === value ? 600 : 400,
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <span style={{ fontSize: '0.9375rem', fontWeight: 600 }}>Headline & stat color</span>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
                    {INK_SWATCHES.map(({ hex, bordered }) => (
                      <button
                        key={hex}
                        aria-label={hex}
                        onClick={() => setColor(hex)}
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 6,
                          background: hex,
                          cursor: 'pointer',
                          border: bordered ? '1px solid #d1d5db' : 'none',
                          boxShadow: color === hex ? '0 0 0 2px #fff, 0 0 0 4px #0454AE' : undefined,
                        }}
                      />
                    ))}
                  </div>
                  {fellBack && <p style={{ fontSize: '0.78125rem', color: '#0B6D5E', margin: '8px 0 0' }}>{CONTRAST_WARNING}</p>}
                  <p style={{ fontSize: '0.78125rem', color: '#888888', margin: '8px 0 0' }}>
                    Size and color apply to the headline and stat only; all other text stays fixed.
                  </p>
                </div>

                <div>
                  <span style={{ fontSize: '0.9375rem', fontWeight: 600 }}>Image sizes</span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                    {IMAGE_FORMATS.map((format) => {
                      const checked = formats.includes(format)
                      return (
                        <label
                          key={format}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '10px 12px',
                            borderRadius: 6,
                            fontSize: '0.875rem',
                            cursor: 'pointer',
                            background: checked ? 'rgba(4,84,174,0.06)' : '#fff',
                            border: checked ? '1px solid #0454AE' : '1px solid #d1d5db',
                          }}
                        >
                          <Checkbox
                            checked={checked}
                            onChange={() => handleToggleFormat(format)}
                            size="small"
                            sx={{ p: 0 }}
                          />
                          <span>{FORMAT_LABEL[format]}</span>
                          <span style={{ marginLeft: 'auto', fontSize: '0.78125rem', color: '#888888' }}>{FORMAT_DIMENSION_LABEL[format]}</span>
                        </label>
                      )
                    })}
                  </div>
                  <p style={{ fontSize: '0.78125rem', color: '#888888', margin: '8px 0 0' }}>
                    Pick as many as you need — each one downloads as its own PNG.
                  </p>
                </div>

                {error && <p style={{ fontSize: '0.8125rem', color: '#b3261e', margin: 0 }}>{error}</p>}

                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    onClick={handleDownload}
                    disabled={busy}
                    style={{
                      background: '#0454AE',
                      color: '#fff',
                      padding: '11px 18px',
                      borderRadius: 4,
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                      border: 'none',
                      cursor: busy ? 'default' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      opacity: busy ? 0.7 : 1,
                    }}
                  >
                    {busy ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <DownloadIcon sx={{ width: 16, height: 16 }} />}
                    {countLabel}
                  </button>
                  {clipboardSupported && (
                    <button
                      onClick={handleCopyImage}
                      style={{
                        background: 'transparent',
                        border: `1px solid ${copied ? '#0B6D5E' : '#0454AE'}`,
                        color: copied ? '#0B6D5E' : '#0454AE',
                        padding: '10px 16px',
                        borderRadius: 4,
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                      }}
                    >
                      {copied ? 'Image copied' : 'Copy image'}
                    </button>
                  )}
                  <button onClick={handleClose} style={{ background: 'none', border: 'none', color: '#0454AE', textTransform: 'uppercase', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>

          <div style={{ flex: 1, minWidth: 360, background: '#f0f0f0', padding: 32, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.78125rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#888888' }}>Preview</span>
              <div style={{ display: 'flex', gap: 6 }}>
                {formats.map((format) => (
                  <button
                    key={format}
                    onClick={() => setPreviewFormat(format)}
                    style={{
                      borderRadius: 9999,
                      padding: '5px 12px',
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      background: previewFormat === format ? '#0454AE' : '#fff',
                      color: previewFormat === format ? '#fff' : '#4e4e4e',
                      border: `1px solid ${previewFormat === format ? '#0454AE' : '#d1d5db'}`,
                    }}
                  >
                    {FORMAT_LABEL[format]}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ width: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.16)' }}>
              <ShareCard {...cardProps} />
            </div>
          </div>
        </div>
      </Dialog>
      {open &&
        createPortal(
          // Each selected format at its exact pixel width, off-screen, for rasterizeShareCard.
          // The offset lives on this wrapper so it isn't copied onto the captured nodes.
          <div aria-hidden style={{ position: 'fixed', top: 0, left: -100000, pointerEvents: 'none' }}>
            {formats.map((format) => (
              <div
                key={format}
                ref={(el) => {
                  renderTargets.current[format] = el
                }}
                style={{ width: FORMAT_DIMENSIONS[format].width }}
              >
                <ShareCard {...cardProps} format={format} />
              </div>
            ))}
          </div>,
          document.body,
        )}
      {toast && <Toast message={toast} />}
    </>
  )
}

const Toggle = ({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) => (
  <button
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    style={{
      width: 44,
      height: 24,
      borderRadius: 12,
      position: 'relative',
      border: 'none',
      cursor: 'pointer',
      background: checked ? '#0B6D5E' : '#888888',
      flexShrink: 0,
    }}
  >
    <span
      style={{
        position: 'absolute',
        top: 3,
        width: 18,
        height: 18,
        borderRadius: '50%',
        background: '#fff',
        transition: 'left .12s, right .12s',
        left: checked ? undefined : 3,
        right: checked ? 3 : undefined,
      }}
    />
  </button>
)

const ConfirmationPane = ({
  downloaded,
  onDone,
  onEditSettings,
  onPickAnotherSize,
}: {
  downloaded: { name: string }[]
  onDone: () => void
  onEditSettings: () => void
  onPickAnotherSize: (format: ShareCardFormat) => void
}) => (
  <>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', background: '#0B6D5E' }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3}>
        <path d="M20 6L9 17l-5-5" />
      </svg>
    </div>
    <h2 style={{ fontFamily: 'Roobert', fontSize: '1.375rem', fontWeight: 500, margin: 0 }}>
      {downloaded.length === 1 ? 'Image downloaded' : `${downloaded.length} images downloaded`}
    </h2>
    <p style={{ fontSize: '0.875rem', color: '#4e4e4e', margin: 0 }}>
      Saved to your downloads folder. Nothing was published — these files live only on your computer.
    </p>
    <div>
      <span style={{ fontSize: '0.9375rem', fontWeight: 600 }}>Files</span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
        {downloaded.map((f) => (
          <div key={f.name} style={{ background: '#f0f0f0', borderRadius: 6, padding: '10px 12px', fontSize: '0.8125rem', overflowWrap: 'anywhere' }}>
            {f.name}
          </div>
        ))}
      </div>
    </div>
    <div>
      <span style={{ fontSize: '0.9375rem', fontWeight: 600 }}>Need another size?</span>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
        {IMAGE_FORMATS.map((format) => (
          <button
            key={format}
            onClick={() => onPickAnotherSize(format)}
            style={{ background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, padding: '8px 12px', fontSize: '0.8125rem', color: '#4e4e4e', whiteSpace: 'nowrap', cursor: 'pointer' }}
          >
            {FORMAT_LABEL[format]} {FORMAT_DIMENSION_LABEL[format]}
          </button>
        ))}
      </div>
    </div>
    <div style={{ display: 'flex', gap: 16 }}>
      <button onClick={onDone} style={{ background: '#0454AE', color: '#fff', padding: '11px 18px', borderRadius: 4, fontSize: '0.875rem', textTransform: 'uppercase', fontWeight: 600, border: 'none', cursor: 'pointer' }}>
        Done
      </button>
      <button onClick={onEditSettings} style={{ background: 'none', border: 'none', color: '#0454AE', textTransform: 'uppercase', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' }}>
        Edit settings
      </button>
    </div>
  </>
)
